import { openModal } from '../helpers/modals';

const MODAL_ID = 'KlaviyoBisModal';
const KLAVIYO_BIS_URL =
  'https://a.klaviyo.com/client/back-in-stock-subscriptions';

const selectors = {
  trigger: '[data-klaviyo-bis]',
  root: '[data-klaviyo-bis-root]',
  form: 'form',
  email: '[name="email"]',
  content: '[js-content]',
  variantInput: '[name="id"]',
};

let activeInstance = null;
let modalFormBound = false;

function bindModalForm() {
  if (modalFormBound) return;

  const modal = document.getElementById(MODAL_ID);
  const form = modal?.querySelector(`${selectors.root} ${selectors.form}`);

  if (!form) return;

  form.addEventListener('submit', event => {
    event.preventDefault();
    activeInstance?._handleSubmit();
  });

  modalFormBound = true;
}

/**
 * Klaviyo back-in-stock subscriptions for sold-out variants
 *
 * @export
 * @class KlaviyoBis
 */
export default class KlaviyoBis {
  /**
   * @param {HTMLElement} productEl The `[data-product]` container
   * @param {HTMLFormElement} formElement The product form
   */
  constructor(productEl, formElement) {
    this.el = productEl;
    this.formElement = formElement;
    this.trigger = formElement.querySelector(selectors.trigger);

    if (!this.trigger) return;

    bindModalForm();

    this.modal = document.getElementById(MODAL_ID);
    this.root = this.modal?.querySelector(selectors.root);
    this.variantInput = formElement.querySelector(selectors.variantInput);

    if (!this.modal || !this.root || !this.variantInput) return;

    this.companyId = formElement.dataset.klaviyoCompanyId;
    this.productTitle = formElement.dataset.productTitle;
    this.variantTitle = formElement.dataset.variantTitle;
    this.variantCount = Number(formElement.dataset.variantCount) || 1;
    this.content = this.root.querySelector(selectors.content);
    this.contentDefaultText = this.content?.getAttribute('data-text');
    this.form = this.root.querySelector(selectors.form);

    if (
      !this.companyId ||
      !this.content ||
      !this.form ||
      !this.variantInput.value
    ) {
      return;
    }

    this.trigger.addEventListener('click', this._onOpen.bind(this));
    activeInstance = this;
  }

  _getDisplayTitle() {
    return this.variantCount <= 1 ? this.productTitle : this.variantTitle;
  }

  _onOpen(event) {
    event.preventDefault();

    const variantId = this.variantInput.value;
    if (!variantId) return;

    openModal(MODAL_ID);
    activeInstance = this;

    this.root.classList.toggle(
      'is-submitted',
      !!localStorage.getItem(variantId),
    );

    this.content.innerHTML = this.contentDefaultText.replace(
      '{variant}',
      `<span class="font-semibold">${this._getDisplayTitle()}</span>`,
    );
  }

  _handleSubmit() {
    const variantId = this.variantInput.value;
    const formattedVariantId = `$shopify:::$default:::${variantId}`;
    const email = this.form.querySelector(selectors.email).value;

    fetch(KLAVIYO_BIS_URL, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        revision: '2024-07-15',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            company_id: this.companyId,
            profile: {
              data: {
                type: 'profile',
                attributes: { email },
              },
            },
            channels: ['EMAIL'],
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: formattedVariantId,
              },
            },
          },
        },
      }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Network response was not ok ${response.statusText}`);
        }

        if (response.status === 202) {
          return { status: 'Accepted' };
        }

        return response.json();
      })
      .then(() => {
        this.root.classList.toggle('is-submitted');
        localStorage.setItem(variantId, 'BIS');
      })
      .catch(error => {
        console.error('There was a problem with the fetch operation:', error);
      });
  }
}
